import { useEffect, useRef, useState } from 'react'
import { Box, IconButton, Menu, MenuItem, Paper, Switch } from '@mui/material'
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined'
import moment from 'moment'
import SectionTitle from '../../../components/common/SectionTitle'
import ClassLocationOverwriteForm from './ClassLocationOverwriteForm'
import LocalClassDisplay from './LocalClassDisplay'
import ClientService from '../../../services/ClientService'
import LocationService from '../../../services/LocationService'
import { handleTrackError, trackEvent } from '../../../services/AmplitudeService'
import CurrentClientProvider from '../../../providers/CurrentClientProvider'

type LocationLink = {
  ClientId?: number | string
  client_location_id?: number | string
  clientclass_id?: number | string
  clientclassid?: number | string
  locationclassid?: number | string
  id?: number | string
  isOverride?: boolean
  deleted_at?: string | null
  [key: string]: any
}

type Props = {
  location?: any
  clientClass?: any
  locationClasses?: any[]
  categories?: any[]
  labels?: any
  isAdmin?: boolean
  visualVariant?: string
  onSave: (updatedLink: any) => void
}

function ClientLocationClassLinkEdit(props: Props) {
  const { location, clientClass, locationClasses, categories, labels, isAdmin = false } = props
  const isV3Variant = props?.visualVariant === 'v3'
  const [localOverrides, setLocalOverrides] = useState<LocationLink>({})
  const currentClient = CurrentClientProvider.useClient() as any
  const [openMore, setOpenMore] = useState(false)
  const moreElement = useRef<HTMLElement | null>(null)

  const selectionKey = `${location?.client_location_id ?? location?.id ?? ''}:${
    clientClass?.locationclassid ?? clientClass?.id ?? ''
  }`

  // Merge any optimistic local changes on top of the latest data from the parent.
  const classLocationLink: LocationLink | null = clientClass ? { ...clientClass, ...localOverrides } : null

  async function deleteData() {
    if (!classLocationLink) {
      return
    }

    try {
      await LocationService.deleteLocationClassById(
        classLocationLink.ClientId ?? currentClient?.client?.Id,
        classLocationLink.locationclassid
      )
      setOpenMore(false)
      const nextLink: LocationLink = {
        ...classLocationLink,
        isActive: false,
        deleted_at: moment().format('YYYY-MM-DD HH:mm:ss'),
      }
      setLocalOverrides((prev) => ({ ...prev, isActive: false, deleted_at: nextLink.deleted_at }))
      props.onSave(nextLink)

      trackEvent('Client Location Classes Event', {
        action: 'Deleted Location Class',
        clientCode: currentClient?.client?.ClientCode,
      })
    } catch (e) {
      console.log(e)
      handleTrackError({
        error: e,
        pageName: 'Client Classes',
        additionalMetadata: {
          clientCode: currentClient?.client?.ClientCode,
          action: 'Deleted Location Class',
        },
      })
    }
  }

  async function undeleteData() {
    if (!classLocationLink) {
      return
    }

    try {
      await LocationService.undeleteLocationClassById(
        classLocationLink.ClientId ?? currentClient?.client?.Id,
        classLocationLink.locationclassid
      )
      setOpenMore(false)
      const nextLink: LocationLink = {
        ...classLocationLink,
        isActive: true,
        deleted_at: null,
      }
      setLocalOverrides((prev) => ({ ...prev, isActive: true, deleted_at: null }))
      props.onSave(nextLink)

      trackEvent('Client Location Classes Event', {
        action: 'Undeleted Location Class',
        clientCode: currentClient?.client?.ClientCode,
      })
    } catch (e) {
      console.log(e)
      handleTrackError({
        error: e,
        pageName: 'Client Classes',
        additionalMetadata: {
          clientCode: currentClient?.client?.ClientCode,
          action: 'Undeleted Location Class',
        },
      })
    }
  }

  useEffect(() => {
    // Only clear optimistic overrides when the actual selection changes (different
    // location or class), not when the parent simply refreshes the same selection's data.
    setLocalOverrides({})
  }, [selectionKey])

  async function switchOverride(event: React.ChangeEvent<HTMLInputElement>) {
    const nextValue = !!event.target.checked

    const clientId = currentClient?.client?.Id ?? classLocationLink?.ClientId ?? clientClass?.client_id
    const locationId =
      classLocationLink?.client_location_id ??
      classLocationLink?.client_locationid ??
      location?.client_location_id ??
      location?.id
    const locationClassId = classLocationLink?.locationclassid ?? clientClass?.locationclassid ?? clientClass?.id

    const nextLink: LocationLink = {
      ...(classLocationLink ?? {}),
      ClientId: clientId,
      client_location_id: locationId,
      locationclassid: locationClassId,
      isOverride: nextValue,
    }

    setLocalOverrides((prev) => ({ ...prev, isOverride: nextValue }))
    props.onSave(nextLink)

    try {
      if (clientId && locationId && locationClassId) {
        await ClientService.toggleOverride(clientId, locationId, locationClassId)
      }
    } catch (e) {
      console.log(e)
      handleTrackError({
        error: e,
        pageName: 'Client Classes',
        additionalMetadata: {
          clientCode: currentClient?.client?.ClientCode,
          action: 'Toggle Location Class Override',
        },
      })
    }
  }

  if (!location || !clientClass || !classLocationLink) {
    return null
  }

  let customStyles: Record<string, string> = {}
  let customTitleStyle: Record<string, string | number> = {}

  if (classLocationLink.isOverride) {
    customStyles = {
      backgroundColor: '#f97125 !important',
      color: '#fff !important',
    }
    customTitleStyle = { color: '#fff !important' }
  }

  if (isV3Variant && !classLocationLink.isOverride) {
    customStyles = {
      background: 'linear-gradient(135deg, #7F56D9 0%, #9E77ED 100%)',
      borderTopLeftRadius: '24px',
      borderTopRightRadius: '24px',
      color: '#fff',
    }
    customTitleStyle = {
      color: '#fff',
      fontWeight: 700,
    }
  }

  return (
    <Paper elevation={2} sx={{ mt: 2 }} className={isV3Variant ? 'v3-profile-summary-card' : undefined}>
      <SectionTitle
        customStyles={customStyles}
        customTitleStyle={customTitleStyle}
        label={
          <Box sx={{ fontSize: 15 }}>
            {`${clientClass.locationclass_name} ${clientClass.clientclass_code}`}
            <Box sx={{ fontSize: 14, color: '#999' }}>{`${location.location_name} class settings`}</Box>
          </Box>
        }
        rightComponent={
          isAdmin ? (
            <Box>
              Override Setting?
              <Switch checked={!!classLocationLink.isOverride} onChange={switchOverride} />
              <IconButton onClick={() => setOpenMore(true)} ref={moreElement as any}>
                <MoreVertOutlinedIcon sx={{ color: 'black' }} />
              </IconButton>
              <Menu open={openMore} anchorEl={moreElement.current} onClose={() => setOpenMore(false)}>
                {!classLocationLink.deleted_at ? (
                  <MenuItem dense onClick={deleteData}>Drop class</MenuItem>
                ) : (
                  <MenuItem dense onClick={undeleteData}>Undelete class</MenuItem>
                )}
              </Menu>
            </Box>
          ) : null
        }
      />

      <Box sx={{ p: 2 }}>
        {classLocationLink.isOverride && isAdmin ? (
          <ClassLocationOverwriteForm
            clientClass={classLocationLink}
            locationClasses={locationClasses}
            onSave={props.onSave}
            categories={categories}
            labels={labels}
            isAdmin={isAdmin}
            client={currentClient?.client}
          />
        ) : (
          <LocalClassDisplay clientClass={clientClass} isAdmin={isAdmin} />
        )}
      </Box>
    </Paper>
  )
}

export default ClientLocationClassLinkEdit